import axios from 'axios';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useCreateSupplyChainInformation } from '@/services/apis/mutations/production-declaration/use-create-supply-chain-information';
import { useGetActorTypes } from '@/services/apis/queries/actor-type/use-get-actor-types';
import { useGetProductionDeclarationSubcontractors } from '@/services/apis/queries/production-declaration/use-get-production-declaration-subcontractors';

type UseProductionDeclarationMaterialSupplyChainDialogParams = {
  commodityOriginId: string;
  materialId: string;
  onOpenChange: (open: boolean) => void;
  productionDeclarationId: string;
  token?: string;
};

export function useProductionDeclarationMaterialSupplyChainDialog({
  commodityOriginId,
  materialId,
  onOpenChange,
  productionDeclarationId,
  token,
}: UseProductionDeclarationMaterialSupplyChainDialogParams) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation('common', {
    useSuspense: false,
  });
  const [selectedSubcontractorId, setSelectedSubcontractorId] = useState<
    string | undefined
  >(undefined);
  const [selectedActorTypeId, setSelectedActorTypeId] = useState<
    string | undefined
  >(undefined);

  const { data: subcontractors = [], isLoading: isLoadingSubcontractors } =
    useGetProductionDeclarationSubcontractors(
      productionDeclarationId,
      materialId,
      commodityOriginId,
      token,
    );

  const { data: actorTypes = [], isLoading: isLoadingActorTypes } =
    useGetActorTypes();

  const { mutateAsync: createSupplyChainInfo, isPending } =
    useCreateSupplyChainInformation();

  const handleOpenChange = useCallback(
    (value: boolean) => {
      if (!value) {
        setSelectedSubcontractorId(undefined);
        setSelectedActorTypeId(undefined);
      }

      onOpenChange(value);
    },
    [onOpenChange],
  );

  const handleSubmit = useCallback(async () => {
    if (!(selectedSubcontractorId && selectedActorTypeId)) {
      return;
    }

    try {
      await createSupplyChainInfo({
        productionDeclarationId,
        materialId,
        commodityOriginId,
        actorId: Number(selectedSubcontractorId),
        actorTypeId: Number(selectedActorTypeId),
        token,
      });

      toast.success(
        t('materialCommodityOrigins.cards.supplyChain.dialog.createSuccess'),
      );
      handleOpenChange(false);
    } catch (error) {
      const description = axios.isAxiosError(error)
        ? (error.response?.data?.message ??
          error.response?.data?.detail ??
          t('materialCommodityOrigins.cards.supplyChain.dialog.createError'))
        : t('materialCommodityOrigins.cards.supplyChain.dialog.createError');

      toast.error(tCommon('error'), { description });
    }
  }, [
    commodityOriginId,
    createSupplyChainInfo,
    handleOpenChange,
    materialId,
    productionDeclarationId,
    selectedActorTypeId,
    selectedSubcontractorId,
    t,
    tCommon,
    token,
  ]);

  return {
    actorTypes,
    handleOpenChange,
    handleSubmit,
    isLoadingActorTypes,
    isLoadingSubcontractors,
    isPending,
    selectedActorTypeId,
    selectedSubcontractorId,
    setSelectedActorTypeId,
    setSelectedSubcontractorId,
    subcontractors,
    t,
    tCommon,
  };
}
